import React, { forwardRef } from "react";

import Link from "next/link";

import Loader, { LoaderOptions, LoaderTypes } from "@stadium/ui/src/components/loaders/loader";

import defaultStyles from "./main.module.scss";

type ButtonStyle =
  | "primary"
  | "primary-outline"
  | "link"
  | "danger"
  | "danger-outline"
  | "success"
  | "success-outline"
  | "light"
  | "rounded-btn"
  | "icon-btn"
  | "no-style";

interface StyledTagProps {
  loaderType?: LoaderTypes;
  isLoading?: boolean;
  loaderOptions?: LoaderOptions[StyledTagProps["loaderType"]];
  styleType?: ButtonStyle;
  styles?: ModularStyles;
  disableOnLoad?: boolean;
  displayChildren?: boolean;
  icon?: string;
  innerChildClass?: string;
}
export type StyledButtonProps = React.ComponentPropsWithoutRef<"button"> & StyledTagProps;
export type StyledLinkProps = React.ComponentPropsWithoutRef<"a"> &
  StyledTagProps & {
    linkProps?: React.ComponentProps<typeof Link>;
    disabled?: boolean;
    prefetch?: boolean;
    disabledOnClick?: StyledLinkProps["onClick"];
    prependWorkspaceSlug?: boolean;
  };
// eslint-disable-next-line no-unused-vars
type getButtonBaseClassNamesMappingType = (styles: ModularStyles) => {
  [T in ButtonStyle]: string;
};

const defaultObj = {};

const getButtonBaseClassNamesMapping: getButtonBaseClassNamesMappingType = (
  styles = defaultStyles,
) => ({
  primary: styles.btnPrimary,

  "primary-outline": styles.btnOutlinePrimary,

  link: styles.btnLink,

  danger: styles.btnDanger,

  "danger-outline": styles.btnOutlineDanger,

  success: styles.btnSuccess,

  "success-outline": styles.btnOutlineSuccess,

  light: styles.btnLight,

  "rounded-btn": styles.roundedBtn,

  "icon-btn": styles.iconBtn,

  "no-style": "",
});

// NOTE:-
// 1. Use it when you want to perform external app navigation and optionally non-navigation actions on click
// 2. for disabled css, pass ".disabled" class (ref - https://getbootstrap.com/docs/4.6/components/buttons/#disabled-state)

export const StyledButton = forwardRef<HTMLButtonElement, StyledButtonProps>(
  (
    {
      loaderType = "buttonLoader",
      isLoading = false,
      loaderOptions,
      styleType = "primary",
      children = "",
      innerChildClass = "",
      onClick,
      className = "",
      styles = defaultStyles,
      disableOnLoad = false,
      disabled,
      displayChildren = false,
      icon,
      ...restProps
    },
    ref,
  ) => {
    const allClassNames = `${getButtonBaseClassNamesMapping(styles)[styleType]} ${className} ${
      isLoading ? styles.loaderWrapper : ""
    }`;
    return (
      <button
        {...restProps}
        disabled={isLoading ? disableOnLoad : disabled}
        className={allClassNames}
        onClick={onClick}
        ref={ref}
        data-loader-type={loaderType}
      >
        <Loader
          displayChildren={displayChildren}
          isLoading={isLoading}
          render={({ [loaderType]: type }) => type(loaderOptions)}
        >
          {displayChildren ? (
            <div
              className={`${isLoading ? styles.hideButtonLabel : styles.buttonLabel} ${
                styles.buttonLabel
              } ${innerChildClass}`}
            >
              {children}
              {icon && <span className={icon} />}
            </div>
          ) : (
            children
          )}
        </Loader>
      </button>
    );
  },
);

const defaultDisabledOnClick: StyledLinkProps["onClick"] = (e) => e.preventDefault();
export const StyledLink = forwardRef<HTMLAnchorElement, StyledLinkProps>(
  (
    {
      loaderType = "buttonLoader",
      isLoading = false,
      loaderOptions,
      styleType = "link",
      children = "",
      onClick,
      className = "",
      innerChildClass = "",
      styles = defaultStyles,
      disableOnLoad = false,
      href,
      displayChildren = false,
      icon,
      linkProps = defaultObj as StyledLinkProps["linkProps"],
      disabledOnClick = defaultDisabledOnClick,
      prefetch = false,
      ...restProps
    },
    ref,
  ) => {
    const { href: linkPropHref = href, passHref = true } = linkProps;
    const allClassNames = `${getButtonBaseClassNamesMapping(styles)[styleType]}  ${className}  ${
      isLoading ? styles.loaderWrapper : ""
    }`;
    const isDisabled = isLoading ? disableOnLoad : restProps.disabled;

    return (
      <Link
        href={linkPropHref}
        passHref={passHref}
        onClick={isDisabled ? disabledOnClick : linkProps.onClick || onClick}
        {...linkProps}
        className={allClassNames}
        prefetch={prefetch}
        ref={ref}
        data-loader-type={loaderType}
        {...restProps}
      >
        <Loader
          displayChildren={displayChildren}
          isLoading={isLoading}
          render={({ [loaderType]: type }) => type(loaderOptions)}
        >
          {displayChildren ? (
            <div
              className={`${
                isLoading ? styles.hideButtonLabel : styles.buttonLabel
              } ${innerChildClass}`}
            >
              {children}
              {icon && <span className={icon} />}
            </div>
          ) : (
            children
          )}
        </Loader>
      </Link>
    );
  },
);

export default StyledButton;
